import Head from "next/head";
import Link from "next/link";
import dynamic from "next/dynamic";
import { getRandom } from "../../utils/blogUtils";
import blogData from "../../blogs.json";
import Service2 from "../../components/services/Service2";
const Capabilities2 = dynamic(() => import("../../components/services/Capabilities2"), { ssr: false });
import SuccessStoryEmbed from "../../components/trust/SuccessStoryEmbed";
import ServiceFAQ from "../../components/services/ServiceFAQ";
import { CustomerSection } from "../../components/ai-agent-demo/CustomerSection";
const Footer = dynamic(() => import("../../components/Footer"), { ssr: false });

ManagedServices.title = "Managed IT Services & Operations Support India | ITMTB";
ManagedServices.desc =
  "Managed IT operations for Indian companies with live systems and no internal tech team — yearly engagements with weekly reporting, defined escalation handling, and proactive monitoring across application, infrastructure, and AI components.";
ManagedServices.header_img = "https://www.itmtb.com/services/capability/managed-services.webp";
ManagedServices.keywords =
  "managed IT services India enterprise, end-to-end IT management services, IT operations support India, managed services India enterprise, yearly managed services engagement India, application monitoring production support India, IT operations outsourcing India, DevOps managed services India, 24x7 IT support India, infrastructure monitoring India";

const faqItems = [
  {
    q: "How is your managed services delivery different from a typical offshore IT support vendor?",
    a: "ITMTB managed services are structured around defined operational cadences rather than reactive ticket resolution. Every engagement includes weekly status reporting covering open incidents, prioritised backlog, deployment activity, infrastructure cost trends, and planned changes for the coming week. Monthly and quarterly reviews are structured — with a dedicated roadmap discussion at each quarterly cycle. Teams maintain written incident response runbooks specific to each system, and escalation paths are defined before incidents occur. For systems that include AI or automation components, monitoring extends to output quality sampling, API schema drift, and inference cost anomalies — failure modes that do not surface in standard infrastructure metrics. The result is an engagement where your operations team has predictable, documented visibility rather than ad-hoc incident notifications.",
  },
  {
    q: "What does a managed services engagement typically include?",
    a: "A managed services engagement at ITMTB typically covers: application monitoring, production support and issue resolution, maintenance and bug fixes, infrastructure support, security updates, enhancement planning, operational reporting, issue prioritisation, and release coordination. The exact scope is defined during the initial engagement planning phase and formalised in a services agreement. Engagements also include governance structure: weekly reporting cadence, task scoping workflows, escalation handling procedures, roadmap discussions, and operational review schedules — the layer that determines how work is prioritised, escalated, and reported, not just whether individual incidents are closed.",
  },
  {
    q: "How do you handle 24×7 monitoring and incident response for Indian enterprises?",
    a: "24×7 monitoring is implemented through a combination of automated alerting and on-call rotation. For infrastructure: we configure alerting in your cloud environment (AWS CloudWatch, Azure Monitor, GCP Cloud Monitoring) with thresholds for availability, latency, error rate, and cost. Critical alerts page an on-call engineer immediately; non-critical alerts are aggregated into a daily review. For AI systems: we add LLM-specific monitoring (output quality sampling, prompt injection detection, inference cost) on top of standard infrastructure metrics. Incident response: every production system we manage has a written incident response runbook specific to its failure modes — not a generic process. Response SLA is defined in the managed services agreement; critical production incidents receive an immediate response within the agreed window. Reporting: monthly operational reports covering uptime, incident summary, and performance trends.",
  },
  {
    q: "Can you take over operations for a system that was built by another team?",
    a: "Yes — but the transition requires a structured handover process. We conduct a system audit during transition: architecture review, dependency mapping, documentation gap assessment, and a shadow period where we observe the system under normal conditions before taking operational responsibility. This is important because undocumented behaviour and tribal knowledge are the largest risks when transitioning operations between teams. For AI systems specifically, we need to understand the training data assumptions, any known failure modes, and any manual interventions the previous team was performing regularly. After the audit, we produce an updated operations runbook and take over responsibility under a defined SLA. We have completed operational transitions for production AI systems, cloud infrastructure, and enterprise applications — including systems with limited documentation.",
  },
  {
    q: "How do you apply automation in managed services without compromising operational governance?",
    a: "Automation is applied selectively to tasks where it reduces routine overhead without introducing operational risk. ITMTB uses Orchestrik for specific automation activities including monitoring workflows, support routing, issue triaging, and coordination of repetitive operational processes. The goal is to reduce manual coordination overhead and processing delays — not to automate decision-making or replace the governance layer. Escalation handling, prioritisation decisions, and roadmap discussions remain human-led. For each automation applied, we document what is automated, the trigger conditions, the expected behaviour, and the escalation path when the automation encounters an unexpected state. Automation is transparent and auditable, not a black box running alongside your operations.",
  },
  {
    q: "What does managing AI components in production actually involve?",
    a: "Running AI components in production requires monitoring for failure modes that do not trigger conventional alerting. Model drift: model behaviour changes when the underlying LLM is updated by the provider, or when the distribution of inputs changes over time. We monitor for output distribution shifts that indicate the system's behaviour has changed materially. Prompt injection risks: production AI systems receive inputs from the external world; some of those inputs will attempt to redirect system behaviour. We monitor logs for injection patterns and alert when unusual sequences occur. API dependency changes: AI systems depend on external APIs whose response schemas change. We monitor for schema drift and alert before it causes failures. Inference cost spikes: AI systems with variable-length outputs or multi-step reasoning can generate unexpected cost spikes. We implement cost guardrails and alert on spend anomalies. For a global intelligence firm, we deployed and have operated four production AI models covering automated risk reports, document analysis, and business intelligence — all in continuous operation.",
  },
  {
    q: "What is a warranty period and when does it apply?",
    a: "A warranty period is a defined window after production deployment during which the build team fixes bugs and regressions at zero additional cost. On ITMTB engagements where we have built the system, the warranty period is 4–8 weeks post-handoff. This covers: bugs in the delivered software, regressions in previously working functionality, and production incidents caused by issues in the delivered code or configuration. The warranty period applies to ITMTB-built systems only. For operational transitions where ITMTB is taking over a system built by another team, the transition audit establishes the operational baseline and SLA scope before operational responsibility is assumed.",
  },
];
ManagedServices.faq = faqItems;

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Managed IT Services & Operations Support",
  "provider": {
    "@type": "Organization",
    "name": "ITMTB",
    "url": "https://www.itmtb.com",
    "logo": "https://www.itmtb.com/logo.webp",
    "sameAs": [
      "https://www.linkedin.com/company/itmtb-technologies/",
      "https://x.com/ITMTBTech",
    ],
  },
  "areaServed": { "@type": "Country", "name": "India" },
  "serviceType": "Managed IT Services",
  "description":
    "IT operations management and managed services for organisations outsourcing technology development and support — yearly engagement models with defined reporting cadences, escalation handling, and automation-assisted workflows.",
  "offers": {
    "@type": "Offer",
    "priceSpecification": {
      "@type": "PriceSpecification",
      "description": "Custom — annual retainer based on scope",
    },
  },
};

const activities = [
  "Application monitoring",
  "Production support",
  "Maintenance & bug fixes",
  "Infrastructure support",
  "Enhancement planning",
  "Security updates",
  "Operational reporting",
  "Issue prioritisation",
  "Release coordination",
];

const cadenceColumns = [
  {
    period: "Weekly",
    items: [
      "Open incidents with current status",
      "Backlog prioritisation changes",
      "Deployments completed in the past week",
      "Infrastructure cost trend",
      "Planned changes for the coming week",
    ],
  },
  {
    period: "Monthly",
    items: [
      "Uptime and availability summary",
      "Incident analysis and resolution review",
      "Patch and security update status",
      "Enhancement pipeline progress",
      "Cost optimisation actions taken",
    ],
  },
  {
    period: "Quarterly",
    items: [
      "Operational priorities review",
      "System requirements assessment",
      "Roadmap discussion and planning",
      "SLA performance review",
      "Optimisation recommendations",
    ],
  },
];

const comparisonRows = [
  {
    label: "Response model",
    col1: "Reactive — you raise a ticket, vendor resolves within SLA",
    col2: "Proactive — continuous monitoring with defined alerting and resolution cadence",
    col3: "Proactive + automated triage and routing for common failure patterns",
  },
  {
    label: "Reporting cadence",
    col1: "On request, or at incident close",
    col2: "Weekly, monthly, and quarterly structured reports",
    col3: "Same, with automated data aggregation for monitoring dashboards",
  },
  {
    label: "Escalation structure",
    col1: "Ad hoc",
    col2: "Written escalation matrix, defined at engagement start",
    col3: "Same",
  },
  {
    label: "Cost management",
    col1: "Not included",
    col2: "Regular cloud cost reviews and optimisation actions",
    col3: "Continuous automated cost monitoring with anomaly alerting",
  },
  {
    label: "Roadmap discussions",
    col1: "Not included",
    col2: "Quarterly, structured around system priorities and enhancement pipeline",
    col3: "Quarterly",
  },
  {
    label: "Engagement model",
    col1: "Per incident or time-and-materials",
    col2: "Annual retainer",
    col3: "Annual retainer",
  },
];

const transitionPhases = [
  {
    phase: "01",
    title: "Audit & Discovery",
    duration: "Weeks 1–2",
    items: [
      "Architecture review and dependency mapping",
      "Documentation gap assessment",
      "Known failure mode and incident history review",
      "Tribal knowledge sessions with the outgoing team",
      "Monitoring and alerting configuration audit",
    ],
  },
  {
    phase: "02",
    title: "Shadow Period",
    duration: "Weeks 3–4",
    items: [
      "Observe system under normal operating conditions",
      "Joint incident handling with current team",
      "Runbook drafting from observed operations",
      "Monitoring threshold calibration",
    ],
  },
  {
    phase: "03",
    title: "Phased Handover",
    duration: "Weeks 5–6",
    items: [
      "Updated operations runbook finalised",
      "SLA definitions agreed and documented",
      "On-call rotation established",
      "First weekly operational report issued",
    ],
  },
  {
    phase: "04",
    title: "Steady-State Operations",
    duration: "Ongoing",
    items: [
      "Weekly reporting cadence active",
      "Monthly operational reviews",
      "Quarterly roadmap discussions",
      "Continuous cost and reliability optimisation cycle",
    ],
  },
];

const subServices = [
  {
    img: null,
    key: "Application, Infrastructure & Website Monitoring",
    title: "Continuous monitoring across your application and infrastructure layer, with alerting configured to your system's specific failure modes.",
    text: "We configure monitoring across cloud environments using AWS CloudWatch, Azure Monitor, and GCP Cloud Monitoring, supplemented by Datadog, Grafana, Prometheus, Sentry, and PagerDuty where appropriate. This covers websites and web applications as well — uptime monitoring, error-rate tracking (4xx/5xx responses), Core Web Vitals, and page performance alongside backend infrastructure health. Alerting is threshold-based and specific to each system — not generic CPU and memory alerts.\n\nFor systems with AI or automation components, monitoring extends to output quality sampling, inference cost trends, and API schema drift — failure modes that do not surface in standard infrastructure metrics. Critical alerts trigger immediate response; non-critical issues are aggregated into the weekly operational review.",
  },
  {
    img: null,
    key: "Production Support & Issue Resolution",
    title: "SLA-defined issue resolution with a written escalation matrix established at the start of each engagement.",
    text: "Production support is structured around a documented escalation matrix — not an ad-hoc ticketing queue. Severity classifications, response windows, and escalation paths are defined before incidents occur. Every system we manage has an incident response runbook specific to its failure modes.\n\nMonthly operational reports summarise incident volume, severity distribution, resolution time, and recurring failure patterns — so you can track whether operational health is improving over time, not just whether individual tickets are closed.",
  },
  {
    img: null,
    key: "Release Engineering & DevOps Operations",
    title: "CI/CD pipeline maintenance, dependency management, security patching, and coordinated release operations.",
    text: "We manage the infrastructure and release engineering that product teams typically treat as overhead: CI/CD pipeline maintenance, dependency and security patch management, environment provisioning, database operations (backups, schema migrations, performance reviews), and coordinated release scheduling.\n\nRelease coordination is included in the weekly operational report — your stakeholders see what is being deployed, when, and what was verified before release.",
  },
  {
    img: null,
    key: "Operational Reporting & Reviews",
    title: "Weekly, monthly, and quarterly operational reporting — structured around what your stakeholders need to track.",
    text: "Weekly reports cover: open incidents with current status, backlog prioritisation changes, deployments in the past week, infrastructure cost trends, and planned changes for the coming week.\n\nMonthly reports cover: uptime and availability, incident analysis, patch and security update status, enhancement pipeline progress, and cost optimisation actions taken.\n\nQuarterly reviews include a roadmap discussion: whether operational priorities were addressed, what has changed in system requirements, and what the operational focus for the next quarter should be.",
  },
  {
    img: null,
    key: "Cost & Reliability Optimisation",
    title: "Regular cost reviews and reliability improvements as a continuous part of managed operations — not a one-off exercise.",
    text: "Cloud infrastructure costs grow faster than usage when not actively managed. As part of managed services, we run regular cost reviews: right-sizing compute resources, implementing storage lifecycle policies, identifying idle endpoints and unused environments, and reviewing infrastructure architecture for reliability improvements.\n\nFor systems with AI components, inference cost management requires specific attention — usage growth, model updates, and architectural changes can produce significant cost changes that are not visible in standard cloud billing reviews.",
  },
  {
    img: null,
    key: "Operational Automation",
    title: "Selective automation of routine operational tasks to reduce manual overhead and improve response consistency.",
    text: "Automation is applied to well-defined operational tasks where it reduces coordination overhead without introducing governance risk. This includes monitoring workflow automation, support request routing, issue triage for common failure patterns, and coordination of repetitive processes.\n\nWhere ITMTB's Orchestrik platform is used for automation, it operates as a separate product layer with its own defined scope and terms. All automation is documented: what is automated, the trigger conditions, expected behaviour, and escalation path for unexpected states. Automation in managed services does not replace operational governance.",
  },
  {
    img: null,
    key: "Warranty Coverage",
    title: "Applicable to systems built by ITMTB — a 4–8 week post-deployment warranty covering bugs and regressions at zero additional cost.",
    text: "For engagements where ITMTB has built the system being managed, a 4–8 week warranty period is included post-deployment. During this window, bugs in the delivered software and regressions in previously working functionality are fixed at no additional charge.\n\nThe warranty period applies to ITMTB-built systems only. For operational transitions where we are taking over a system built by another team, the transition audit establishes the operational baseline and SLA scope before operational responsibility is assumed.",
  },
];

export default function ManagedServices() {
  let insights = blogData.filter(item => !["case-study", "tech"].includes(item?.meta?.category));
  let caseStudies = blogData.filter(item => item?.meta?.category === "case-study");

  let filteredInsights = insights.filter(item => item?.meta?.refer?.includes("Managed Services"));
  let filteredCaseStudies = caseStudies.filter(item => item?.meta?.refer?.includes("Managed Services"));

  if (filteredInsights.length === 1) {
    insights = [...filteredInsights, ...getRandom(insights.filter(item => !filteredInsights.includes(item)), 1)];
  } else if (filteredInsights.length >= 2) {
    insights = getRandom(filteredInsights, 2);
  } else {
    insights = getRandom(insights, 2);
  }

  if (filteredCaseStudies.length === 1) {
    caseStudies = [...filteredCaseStudies, ...getRandom(caseStudies.filter(item => !filteredCaseStudies.includes(item)), 1)];
  } else if (filteredCaseStudies.length >= 2) {
    caseStudies = getRandom(filteredCaseStudies, 2);
  } else {
    caseStudies = getRandom(caseStudies, 2);
  }

  return (
    <div>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      </Head>

      {/* Hero */}
      <Service2
        heading="Managed IT Services & Operations Support India"
        paragraph={`Technology Managed Services means handing off the day-to-day IT operations of your applications, infrastructure, and workflows to a dedicated team — so your organisation can focus on its core business without building or scaling an internal tech team. It is end-to-end IT management: monitoring, support, escalation, and reporting run as one accountable engagement rather than a stack of disconnected vendors.<br/><br/>ITMTB provides Managed Services for companies so they don't have to invest in in-house IT teams for managing their software. We handle monitoring, incident response, escalation, and release coordination so production systems stay stable and accountable.<br/><br/>Engagements include weekly reporting, a written escalation matrix, structured monthly reviews, and quarterly roadmap discussions.`}
        primaryCta={null}
        heroSvg={
          <svg viewBox="0 0 440 400" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-md opacity-70" aria-hidden="true" role="presentation">
            {/* dot grid */}
            {[...Array(6)].map((_, row) =>
              [...Array(7)].map((_, col) => (
                <circle key={`${row}-${col}`} cx={40 + col * 60} cy={40 + row * 60} r="1.5" fill="#e2e8f0" />
              ))
            )}
            {/* cross-connections */}
            <path d="M 90 100 C 140 80 180 68 220 60" stroke="#e2e8f0" strokeWidth="1"/>
            <path d="M 350 100 C 308 80 268 68 220 60" stroke="#e2e8f0" strokeWidth="1"/>
            <path d="M 60 220 C 72 268 96 298 130 330" stroke="#e2e8f0" strokeWidth="1"/>
            <path d="M 380 220 C 368 268 344 298 310 330" stroke="#e2e8f0" strokeWidth="1"/>
            <path d="M 130 330 C 190 345 250 345 310 330" stroke="#e2e8f0" strokeWidth="1"/>
            {/* spokes from center */}
            <path d="M 220 200 C 220 162 220 120 220 60" stroke="#cbd5e1" strokeWidth="1.5"/>
            <path d="M 220 200 C 178 168 138 138 90 100" stroke="#cbd5e1" strokeWidth="1.5"/>
            <path d="M 220 200 C 262 168 302 138 350 100" stroke="#cbd5e1" strokeWidth="1.5"/>
            <path d="M 220 200 C 165 202 115 210 60 220" stroke="#cbd5e1" strokeWidth="1.5"/>
            <path d="M 220 200 C 275 202 325 210 380 220" stroke="#cbd5e1" strokeWidth="1.5"/>
            <path d="M 220 200 C 192 242 165 284 130 330" stroke="#cbd5e1" strokeWidth="1.5"/>
            <path d="M 220 200 C 248 242 272 284 310 330" stroke="#cbd5e1" strokeWidth="1.5"/>
            {/* outer nodes */}
            <circle cx="220" cy="60" r="7" stroke="#94a3b8" strokeWidth="1.5"/>
            <circle cx="90" cy="100" r="9" stroke="#94a3b8" strokeWidth="1.5"/>
            <circle cx="350" cy="100" r="9" stroke="#94a3b8" strokeWidth="1.5"/>
            <circle cx="60" cy="220" r="7" stroke="#cbd5e1" strokeWidth="1.5"/>
            <circle cx="380" cy="220" r="7" stroke="#cbd5e1" strokeWidth="1.5"/>
            <circle cx="130" cy="330" r="9" stroke="#94a3b8" strokeWidth="1.5"/>
            <circle cx="310" cy="330" r="9" stroke="#94a3b8" strokeWidth="1.5"/>
            {/* node inner fills */}
            <circle cx="220" cy="60" r="3" fill="#cbd5e1"/>
            <circle cx="90" cy="100" r="3.5" fill="#cbd5e1"/>
            <circle cx="350" cy="100" r="3.5" fill="#cbd5e1"/>
            <circle cx="130" cy="330" r="3.5" fill="#cbd5e1"/>
            <circle cx="310" cy="330" r="3.5" fill="#cbd5e1"/>
            {/* dashed monitor rings on two outer nodes — monitoring coverage hint */}
            <circle cx="90" cy="100" r="16" stroke="#94a3b8" strokeWidth="0.8" strokeOpacity="0.4" strokeDasharray="3 3"/>
            <circle cx="350" cy="100" r="16" stroke="#94a3b8" strokeWidth="0.8" strokeOpacity="0.4" strokeDasharray="3 3"/>
            <circle cx="130" cy="330" r="16" stroke="#94a3b8" strokeWidth="0.8" strokeOpacity="0.4" strokeDasharray="3 3"/>
            {/* central node — amber accent (ops hub) */}
            <circle cx="220" cy="200" r="24" stroke="#b45309" strokeWidth="1" strokeOpacity="0.3" strokeDasharray="5 3"/>
            <circle cx="220" cy="200" r="18" stroke="#b45309" strokeWidth="2"/>
            <circle cx="220" cy="200" r="7" fill="#b45309" fillOpacity="0.15" stroke="#b45309" strokeWidth="1.5"/>
          </svg>
        }
      />

      {/* § Anchor nav */}
      <nav className="bg-white border-b border-gray-100 px-6 md:px-[60px] py-2 overflow-x-auto">
        <div className="flex gap-6 text-sm font-medium whitespace-nowrap">
          {[
            { label: "Who it's for", href: "#who-its-for" },
            { label: "What's included", href: "#whats-included" },
            { label: "How transitions work", href: "#transitions" },
            { label: "Support models compared", href: "#comparison" },
            { label: "FAQ", href: "#faq" },
          ].map(({ label, href }) => (
            <a key={href} href={href} className="text-gray-500 hover:text-indigo-600 transition-colors py-1">
              {label}
            </a>
          ))}
        </div>
      </nav>

      <div className="-mt-5">
        <CustomerSection title="Trusted by" />
      </div>

      {/* § Who this is for */}
      <section id="who-its-for" className="scroll-mt-24 px-8 md:px-[60px] py-14 bg-white border-t border-gray-100">
        <div className="max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">Who This Is For</h2>
          <div className="space-y-8">
            <div>
              <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-2">
                Companies already working with outsourced software vendors in India
              </h3>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                If a separate vendor built or continues to build your software, the operational layer is usually missing — most build-side agreements stop at delivery. Managed services adds the monitoring, reporting, and escalation structure that keeps production systems accountable to your business rather than to a development backlog.
              </p>
            </div>
            <div>
              <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-2">
                Companies running live websites, portals, applications, or internal tools
              </h3>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                Once a system is in production, uptime, performance, security patching, and cloud cost control become continuous operational obligations. We take ownership of those activities under a yearly engagement, so the systems your customers and staff depend on each day have a defined operations team behind them.
              </p>
            </div>
            <div>
              <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-2">
                Companies where ongoing IT support has become informal or vendor-dependent
              </h3>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                When support consists of WhatsApp messages, ad-hoc tickets, or whoever picks up the phone, incidents stack up quietly and root causes never get addressed. Managed services replaces that informality with a written escalation matrix, a prioritised backlog, and structured weekly reporting — so leadership knows exactly where things stand.
              </p>
            </div>
            <div>
              <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-2">
                Companies that need predictable reporting, escalation, and operational ownership
              </h3>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                CTOs, COOs, and CFOs at mid-sized Indian enterprises need visibility into what is changing, what broke, and what it costs — without chasing it down. Every engagement includes weekly status reports, monthly operational reviews, and quarterly roadmap discussions so operational health is observable at a glance, in board-ready format.
              </p>
            </div>
            <div>
              <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-2">
                Companies without an internal tech team — or not ready to hire one
              </h3>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                Building an internal IT team in India typically means 6–12 months to recruit a CTO and supporting engineers, plus payroll, attrition, and management overhead. A managed services engagement provides equivalent operational coverage immediately, with the option to transition responsibilities to an in-house team later as it scales.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* § Operational reality */}
      <section id="whats-included" className="scroll-mt-24 px-8 md:px-[60px] py-14 bg-white">
        <div className="max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            How Managed Services Reduce Long-Term Operational Burden
          </h2>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
            Software ownership does not end at deployment. Operational continuity often requires monitoring, maintenance, issue resolution, infrastructure support, enhancement planning, and ongoing optimisation.
          </p>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-10">
            ITMTB offers yearly managed-services engagement models focused on operational continuity, predictable support, and lifecycle ownership.
          </p>

          {/* 9-item activity grid */}
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-5">
            Typical managed services activities
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mb-10">
            {activities.map(activity => (
              <div
                key={activity}
                className="flex items-center gap-3 bg-gray-50 border border-gray-100 rounded-lg px-4 py-3"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                <span className="text-sm text-gray-700">{activity}</span>
              </div>
            ))}
          </div>

          <p className="text-gray-700 text-base md:text-lg leading-relaxed">
            Managed-services engagements include weekly reporting, task scoping, prioritisation workflows, escalation handling, roadmap discussions, and operational review cadence — helping organisations reduce operational uncertainty, support fragmentation, and long-term maintenance risk.
          </p>
        </div>
      </section>

      {/* § Operational cadence */}
      <section id="engage" className="px-8 md:px-[60px] py-14 bg-gray-50 border-t border-gray-100">
        <div className="max-w-5xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Operational Cadence</h2>
          <p className="text-gray-600 text-base md:text-lg mb-10">
            Predictable reporting and review structure across every engagement — delivered from our India-based operations team during IST business hours, with 24×7 on-call rotation for production incidents.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {cadenceColumns.map(cadence => (
              <div key={cadence.period} className="bg-white border border-gray-200 rounded-xl p-6">
                <p className="text-xs font-semibold uppercase tracking-widest text-indigo-500 mb-5">
                  {cadence.period}
                </p>
                <ul className="space-y-3">
                  {cadence.items.map(item => (
                    <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/*
            Weekly report image — add when asset is ready.
            Drop the image at ~/Downloads, then move to:
            /public/services/managed-services/sample-weekly-report.webp

            Uncomment and replace this block:

            import Image from "next/future/image";  // add to imports at top

            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 px-6 pt-5 mb-4">
                Sample weekly report
              </p>
              <Image
                src="/services/managed-services/sample-weekly-report.webp"
                alt="Sample weekly operational report"
                width={1200}
                height={800}
                className="w-full h-auto"
              />
            </div>
          */}
        </div>
      </section>

      {/* § Transition & Takeover Plan */}
      <section id="transitions" className="scroll-mt-24 px-8 md:px-[60px] py-14 bg-gray-50 border-t border-gray-100">
        <div className="max-w-5xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            How We Transition Operations from Another Provider
          </h2>
          <p className="text-gray-600 text-base md:text-lg mb-10">
            A structured four-phase process that establishes operational clarity before responsibility is transferred.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {transitionPhases.map(phase => (
              <div key={phase.phase} className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="flex items-start gap-4 mb-5">
                  <span className="text-3xl font-bold text-indigo-200 leading-none select-none">
                    {phase.phase}
                  </span>
                  <div>
                    <h3 className="text-base font-bold text-gray-900">{phase.title}</h3>
                    <p className="text-xs text-indigo-500 font-medium mt-1">{phase.duration}</p>
                  </div>
                </div>
                <ul className="space-y-2.5">
                  {phase.items.map(item => (
                    <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="text-gray-600 text-base md:text-lg mt-8">
            For a concrete example, see how we{" "}
            <Link href="/blog/d2c-retail-technology-management-takeover">
              <a className="text-indigo-600 hover:text-indigo-800 font-medium transition-colors">
                took over an undocumented D2C retail technology stack and turned it into a managed operation
              </a>
            </Link>{" "}
            — documentation, cloud migration, deployment modernization, and operations automation run as one engagement.
          </p>
        </div>
      </section>

      {/* Success stories */}
      <SuccessStoryEmbed
        referTag="Managed Services"
        maxItems={3}
        heading="Selected managed services engagements"
      />

      {/* § Automation & AI as efficiency layer */}
      <section className="px-8 md:px-[60px] py-14 bg-white border-t border-gray-100">
        <div className="max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            How ITMTB uses automation to improve Managed Services Economics
          </h2>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
            Managed services are increasingly evolving through automation, operational orchestration, and AI-assisted workflows.
          </p>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
            ITMTB uses{" "}
            <Link href="/orchestrik">
              <a className="text-indigo-600 hover:text-indigo-800 font-medium transition-colors">
                Orchestrik
              </a>
            </Link>{" "}
            for selected automation activities where appropriate. These may include monitoring workflows, repetitive operational processes, support routing, issue triaging, and operational coordination. Orchestrik is a separate product with its own pricing and terms.
          </p>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
            The goal is not replacing operational governance. The goal is reducing repetitive operational overhead while improving response efficiency and visibility.
          </p>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed">
            Over time, operational automation can help reduce manual support load, repetitive coordination effort, and support-processing delays — extending operational capabilities into the managed services layer without replacing governance.
          </p>
        </div>
      </section>

      {/* § Comparison table */}
      <section id="comparison" className="scroll-mt-24 px-8 md:px-[60px] py-14 bg-white border-t border-gray-100">
        <div className="max-w-6xl overflow-x-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            Managed Services vs Ticket-Based Support: What&apos;s the Difference?
          </h2>
          <p className="text-gray-600 text-base mb-8">
            The structural differences between reactive ticket-based support and a managed operations engagement — across response model, reporting cadence, escalation structure, and cost management.
          </p>
          <table className="w-full border-collapse text-sm min-w-[640px]">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th className="text-left py-3 pr-6 w-[20%]" />
                <th className="text-left py-3 pr-6 text-gray-900 font-semibold w-[26%]">
                  Ticket-Based Support
                </th>
                <th className="text-left py-4 pr-6 text-indigo-900 font-semibold w-[27%] bg-indigo-50 px-4">
                  Managed Services
                </th>
                <th className="text-left py-3 text-gray-900 font-semibold w-[27%]">
                  Managed Services
                  <br />
                  with Automation
                </th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row, idx) => (
                <tr
                  key={row.label}
                  className={idx % 2 === 0 ? "bg-gray-50/40" : "bg-white"}
                >
                  <td className="py-4 pr-6 text-gray-500 font-medium text-xs uppercase tracking-wide align-top border-b border-gray-100">
                    {row.label}
                  </td>
                  <td className="py-4 pr-6 text-gray-600 leading-snug align-top border-b border-gray-100">
                    {row.col1}
                  </td>
                  <td className="py-4 pr-6 text-gray-800 leading-snug bg-indigo-50/60 px-4 align-top font-medium border-b border-indigo-100">
                    {row.col2}
                  </td>
                  <td className="py-4 text-gray-600 leading-snug align-top border-b border-gray-100">
                    {row.col3}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* § Engagement model & pricing methodology */}
      <section className="px-8 md:px-[60px] py-14 bg-white border-t border-gray-100">
        <div className="max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">Engagement Model</h2>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
            ITMTB offers yearly managed-services engagement models focused on operational continuity, predictable support, and lifecycle ownership — delivered by India-based engineering and operations teams. Yearly engagements provide the operational stability required for consistent reporting cadences, structured reviews, and compounding optimisation.
          </p>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
            Engagement scope is defined through an initial discovery conversation. Pricing is driven by system complexity, monitoring surface, support volume, and reporting requirements — all assessed before a proposal is prepared.
          </p>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed">
            Weekly reporting, task scoping workflows, prioritisation and escalation procedures, and quarterly roadmap discussions are included as standard — not as add-ons. Contracting, invoicing, and GST treatment follow standard India MSA terms.
          </p>
        </div>
      </section>

      {/* Capabilities */}
      <Capabilities2
        dataList={subServices}
        bg="bg-[#402A6A]"
        insights={[...caseStudies, ...insights]}
        hideFooter
        hideCardSubtitle
      />

      {/* § Related capabilities */}
      <section className="px-8 md:px-[60px] py-14 bg-gray-50 border-t border-gray-100">
        <div className="max-w-5xl">
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-7">
            Related capabilities
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                href: "/services/custom-software-development",
                label: "Custom Software Development",
                desc: "Our core engineering practice — the systems we operate are often the ones we built. Greenfield platforms, customer-facing and operational systems, and AI-enabled products.",
              },
              {
                href: "/services/cloud",
                label: "Cloud Infrastructure",
                desc: "Architecture, migration, and operations for the cloud layer your applications run on.",
              },
              {
                href: "/services/cybersecurity",
                label: "Cybersecurity",
                desc: "Security operations, compliance monitoring, and risk management integrated into managed services.",
              },
              {
                href: "/agentic-ai",
                label: "AI Agent Deployment",
                desc: "Deploying and operating AI agent systems in production — with managed operations included.",
              },
              {
                href: "/services/data-center-project-readiness-governance",
                label: "Data Centre Readiness",
                desc: "Operations readiness and handover governance for organisations planning a captive, DR, or hybrid data centre setup.",
              },
            ].map(link => (
              <Link key={link.href} href={link.href}>
                <a className="block bg-white border border-gray-200 rounded-xl p-6 hover:border-indigo-400 hover:shadow-sm transition-all group">
                  <h3 className="text-base font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors mb-2">
                    {link.label}
                  </h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{link.desc}</p>
                  <span className="mt-4 inline-block text-sm font-semibold text-indigo-500 group-hover:text-indigo-700 transition-colors">
                    Learn more →
                  </span>
                </a>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <div id="faq" className="scroll-mt-24">
        <ServiceFAQ items={faqItems} />
      </div>

      {/* Page-specific CTA — replaces generic ContactUsHome */}
      <section className="px-8 md:px-[60px] py-16 bg-[#0d0822]">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-indigo-400 mb-5">
            Start the conversation
          </p>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-5">
            Discuss a Managed Services Engagement
          </h2>
          <p className="text-slate-300 text-base md:text-lg leading-relaxed mb-8">
            We assess your current operational setup, define scope, and prepare a
            proposal — no default contracts, no minimum headcount. Scope is driven
            by your system complexity and reporting requirements.
          </p>
          <Link href="/contact-us?utm_source=service-managed-services&utm_content=bottom-cta">
            <a className="inline-block bg-[#f1aa4e] text-black font-semibold text-base px-8 py-3.5 rounded-md hover:bg-[#e09a3e] transition-colors">
              Request support model and pricing →
            </a>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
